import { Box, Image, Text } from '@chakra-ui/react'
import styled from '@emotion/styled'

import { InteractiveBox } from 'components/ui/InteractiveBox'
import leftArrow from 'public/icons/arrow-left.svg'
import calendar from 'public/icons/calendar.svg'
import camera from 'public/icons/camera.svg'
import envelope from 'public/icons/envelope.svg'
import infoCircle from 'public/icons/info-circle.svg'
import key from 'public/icons/key.svg'
import pin from 'public/icons/map-pin.svg'
import money from 'public/icons/money.svg'
import openDoor from 'public/icons/open_door.svg'
import search from 'public/icons/search.svg'
import sparkle from 'public/icons/sparkle.svg'
import umbrella from 'public/icons/umbrella-simple.svg'
import padlock from 'public/icons/unlocked-padlock.svg'
import theme from 'styles/theme'

import { RectButton } from '../Buttons/RectButton'

import { ItemCardStylingProps } from './interfaces'

const getItemCardWrapperBgColor = ({ isBundle }: ItemCardStylingProps): string => {
  if (isBundle) {
    return theme.token('colors.green.a150')
  }

  return theme.token('colors.green.a50')
}

export const ItemCardWrapper = styled(InteractiveBox)<ItemCardStylingProps>`
  && {
    position: relative;
    padding: 20px;
    background-color: ${(props) => getItemCardWrapperBgColor(props)};
  }
`

export const ItemCardDetails = styled(Box)`
  display: flex;
  word-break: break-word;
`

export const ItemCardActions = styled(Box)`
  margin-left: 20px;

  > * + * {
    margin-top: 8px;
  }
`

export const ItemCardThumbnail = styled(Image)`
  padding: 5px;
  border-radius: ${theme.token.var('borderRadius.small')};
  background: ${theme.token.var('colors.white')};
  object-fit: contain;
`
const getItemCardTextColor = ({ isBundle }: ItemCardStylingProps): string => {
  if (isBundle) {
    return theme.token('colors.white')
  }

  return 'unset'
}

const getItemCardSecondaryColor = ({ isBundle }: ItemCardStylingProps): string => {
  if (isBundle) {
    return theme.token('colors.green.a250')
  }

  return theme.token('colors.green.a200')
}

const getItemCardIconWrapperBgColor = ({ isBundle }: ItemCardStylingProps): string => {
  if (isBundle) {
    return theme.token('colors.green.a175')
  }

  return theme.token('colors.white')
}

export const ItemCardText = styled(Text)<ItemCardStylingProps>`
  color: ${(props) => getItemCardTextColor(props)};
`

export const ItemCardDisabledSpan = styled('span')<ItemCardStylingProps>`
  color: ${(props) => getItemCardTextColor(props)};
  opacity: 0.6;
`

export const ItemCardSecondarySpan = styled('span')<ItemCardStylingProps>`
  color: ${(props) => getItemCardSecondaryColor(props)};
`

export const ItemCardIconWrapper = styled(Box)<ItemCardStylingProps>`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-right: 8px;

  position: relative;

  background-color: ${(props) => getItemCardIconWrapperBgColor(props)};
  border-radius: 50%;
  color: ${(props) => getItemCardSecondaryColor(props)};

  > * {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  p {
    color: ${(props) => getItemCardSecondaryColor(props)};
  }
`

export const PinIcon = styled(pin)<ItemCardStylingProps>`
  width: 12px;

  path {
    fill: ${(props) => getItemCardSecondaryColor(props)};
  }
`

export const SearchIcon = styled(search)<ItemCardStylingProps>`
  width: 12px;

  path {
    fill: ${(props) => getItemCardSecondaryColor(props)};
  }
`

export const MoneyIcon = styled(money)<ItemCardStylingProps>`
  width: 12px;

  path {
    stroke: ${(props) => getItemCardSecondaryColor(props)};
  }
`

export const CalendarIcon = styled(calendar)<ItemCardStylingProps>`
  width: 11px;

  path {
    fill: ${(props) => getItemCardSecondaryColor(props)};
  }
`

export const EnvelopeIcon = styled(envelope)<ItemCardStylingProps>`
  width: 11px;

  path {
    fill: ${(props) => getItemCardSecondaryColor(props)};
  }
`

export const InfoCircle = styled(infoCircle)<ItemCardStylingProps>`
  width: 11px;

  path {
    fill: ${(props) => getItemCardSecondaryColor(props)};
  }
`

export const UmbrellaIcon = styled(umbrella)<ItemCardStylingProps>`
  width: 11px;

  path {
    stroke: ${(props) => getItemCardSecondaryColor(props)};
  }
`

export const SparklesIcon = styled(sparkle)<ItemCardStylingProps>`
  width: 11px;

  path {
    stroke: ${(props) => getItemCardSecondaryColor(props)};
  }
`

export const ItemCardAmountButton = styled(RectButton)`
  display: inline-flex;
  margin: 0 10px;
`

export const ItemCardActionButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-left: -4px;
  margin-right: -4px;

  > * {
    margin-left: 4px;
    margin-right: 4px;
  }
`

const ItemCardActionButton = styled(RectButton)`
  width: 40px;
  height: 40px;

  svg {
    width: 20px;
    height: 20px;
  }
`

export const ItemCardEditButton = styled(ItemCardActionButton)`
  background-color: ${theme.token.var('colors.green.a200')};
  color: ${theme.token.var('colors.white')};
`

export const ItemCardDeleteButton = styled(ItemCardActionButton)`
  background-color: ${theme.token.var('colors.green.a175')};
  color: ${theme.token.var('colors.green.a250')};
`

export const OrderCardDeleteButton = styled(ItemCardActionButton)`
  background-color: ${theme.token.var('colors.green.a175')};
  border: 2px solid ${theme.token.var('colors.green.a50')};
  color: ${theme.token.var('colors.green.a250')};
`

const getCardBackgroundColor = (props: any): string => {
  if (props.backgroundColor) {
    return props.backgroundColor
  }

  return theme.token('colors.white')
}

export const CardActionButton = styled(RectButton)`
  width: 30px;
  height: 30px;

  background-color: ${(props) => getCardBackgroundColor(props)};
  border: 2px solid ${theme.token.var('colors.green.a200')};
  color: ${theme.token.var('colors.green.a250')};

  svg {
    width: 20px;
    height: 20px;
  }
`

export const CardActionButtonWithoutFill = styled(RectButton)`
  width: 30px;
  height: 30px;

  background-color: ${(props) => getCardBackgroundColor(props)};
  border: 2px solid ${theme.token.var('colors.green.a200')};

  svg {
    width: 20px;
    height: 20px;
  }
`

export const NewPinIcon = styled(pin)<{ color: string }>`
  width: 12px;

  path {
    fill: ${(props) => props.color};
  }
`
export const NewCalendarIcon = styled(calendar)<{ color: string }>`
  width: 11px;

  path {
    fill: ${(props) => props.color};
  }
`

export const NewKeyIcon = styled(key)<{ color: string }>`
  width: 24px;

  path {
    fill: ${(props) => props.color};
  }
`

export const NewPadlockIcon = styled(padlock)<{ color: string }>`
  width: 24px;
  height: 24px;

  path {
    stroke: ${(props) => props.color};
  }

  circle {
    fill: ${(props) => props.color};
  }
`

export const NewCameraIcon = styled(camera)<{ color: string }>`
  width: 24px;
  height: 24px;

  path {
    fill: ${(props) => props.color};
  }
`

export const NewUmbrellaIcon = styled(umbrella)<{ color: string }>`
  width: 12px;

  path {
    stroke: ${(props) => props.color};
  }
`

export const NewSparklesIcon = styled(sparkle)<{ color: string }>`
  width: 12px;

  path {
    stroke: ${(props) => props.color};
  }
`

export const NewOpenDoorIcon = styled(openDoor)<{ color: string }>`
  width: 24px;
  height: 24px;

  path {
    stroke: ${(props) => props.color};
  }
`

export const NewLeftArrowIcon = styled(leftArrow)<{ color: string }>`
  width: 24px;
  height: 24px;

  path {
    stroke: ${(props) => props.color};
  }
`
